import type { BoxProps } from '@mui/material/Box';

import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { useTranslate } from 'src/locales';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function DealerCreditInfo({ sx, ...other }: BoxProps) {
  const { t } = useTranslate('layout');

  const { user } = useAuthContext();

  const availableCredit = user?.availableCredit ?? 0;
  const totalCreditLimit = 100000; // This could come from API in the future
  const creditUsagePercent = ((totalCreditLimit - availableCredit) / totalCreditLimit) * 100;

  // Format currency in Indian Rupees
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <Box
      sx={[
        (theme) => ({
          px: 3,
          py: 4,
          borderRadius: 2,
          position: 'relative',
          bgcolor: varAlpha(theme.vars.palette.primary.mainChannel, 0.08),
          border: `solid 1px ${varAlpha(theme.vars.palette.primary.mainChannel, 0.24)}`,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
          {t('dealerCreditInfo.availableCredit')}
        </Typography>

        <Typography variant="h4" sx={{ color: 'primary.main', mb: 2 }}>
          {formatCurrency(availableCredit)}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('dealerCreditInfo.creditUsed')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {creditUsagePercent.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={creditUsagePercent}
            sx={{
              height: 6,
              borderRadius: 1,
              bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.12),
            }}
          />
        </Box>

        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5 }}>
          {t('dealerCreditInfo.limit', { amount: formatCurrency(totalCreditLimit) })}
        </Typography>
      </Box>
    </Box>
  );
}
